// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import RfqQuoteDetailsProposedDetailsGridItem, {
    RfqQuoteDetailsProposedDetailsGridItemStyles,
} from "@rugby/Overrides/Widgets/RfqQuoteDetails/RfqQuoteDetailsProposedDetailsGridItem";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    addLinesToCart,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsProposedDetailsGridStyles {
    mainWrapper?: InjectableCss;
    headerWrapper?: InjectableCss;
    countLabelText?: TypographyPresentationProps;
    countValueText?: TypographyPresentationProps;
    itemStyles?: RfqQuoteDetailsProposedDetailsGridItemStyles;
    footerWrapper?: InjectableCss;
    totalLabelText?: TypographyPresentationProps;
    totalValueText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    totalWithoutVatText?: TypographyPresentationProps;
    addSelectedContainer?: InjectableCss;
    addSelectedButton?: ButtonPresentationProps;
    selectAllButton?: CheckboxPresentationProps;
}

export const rfqQuoteDetailsProposedDetailsGridStyles: RfqQuoteDetailsProposedDetailsGridStyles = {
    headerWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 15px;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    addSelectedContainer: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    selectAllButton: {
        css: css`
            padding-right: 15px;
        `,
    },
    countLabelText: {
        weight: 600,
        css: css`
            margin-left: 5px;
        `,
    },
    countValueText: {
        weight: 600,
    },
    footerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            justify-content: flex-end;
            margin-top: 20px;
        `,
    },
    totalLabelText: {
        size: 20,
        weight: "bold",
        css: css`
            margin-right: 10px;
        `,
    },
    totalValueText: {
        size: 20,
    },
    vatLabelText: {
        size: 12,
    },
    totalWithoutVatText: {
        size: 20,
        css: css`
            margin-top: 5px;
        `,
    },
};

const styles = rfqQuoteDetailsProposedDetailsGridStyles;

const RfqQuoteDetailsProposedDetailsGrid = ({
    quoteState,
    enableVat,
    vatPriceDisplay,
    addLinesToCart,
    showPricing,
}: Props) => {
    const quote = quoteState.value;
    const quoteLines = quote?.quoteLineCollection;
    const toasterContext = useContext(ToasterContext);
    const [checkedItems, setCheckedItems] = useState({} as { [id: string]: boolean });
    const [selectAllState, setSelectAllState] = useState(false);

    // XNG-Changes
    useEffect(() => {
        const nonRestrictedLines = quoteLines?.filter(o => !o.isRestricted && o.canAddToCart) || [];
        const allChecked = nonRestrictedLines.length > 0 && nonRestrictedLines.every(o => checkedItems[o.id]);

        setSelectAllState(allChecked);
    }, [checkedItems, quoteLines]);

    if (!quote || !quoteLines) {
        return null;
    }

    const isDisabledAddSelected =
        Object.keys(quoteLines).length === 0 || Object.values(checkedItems).every(item => !item);

    // XNG-Changes
    const onSelectAllItems = () => {
        const nonRestrictedIds = quoteLines
            ?.filter(o => !o.isRestricted && o.canAddToCart)
            .reduce((selected, o) => ({ ...selected, [o.id]: !selectAllState }), {});

        setCheckedItems(nonRestrictedIds);
        setSelectAllState(!selectAllState);
    };

    const onSelectItem = (id: string) => {
        setCheckedItems((items: any) => ({ ...items, [id]: !items[id] }));
    };

    const onClickAddSelected = () => {
        // if (quote.isJobQuote) {
        //     acceptJobQuote({
        //         quote,
        //         onSuccess: () => {
        //             toasterContext.addToast({ body: "Quote accepted", messageType: "success" });
        //             redirectToJobQuotes();
        //         },
        //         onComplete(resultProps) {
        //             if (resultProps.apiResult) {
        //                 // "this" is targeting the object being created, not the parent SFC
        //                 // eslint-disable-next-line react/no-this-in-sfc
        //                 this.onSuccess?.();
        //             }
        //         },
        //     });
        // } else {
        // const cartLines =
        //     quote.cartLines &&
        //     quote.cartLines
        //         .filter(o => checkedItems[o.id])
        //         .map(
        //             o =>
        //                 ({
        //                     ...o,
        //                     properties: {
        //                         ...o.properties,
        //                         QuoteNumber: quote.quoteNumber,
        //                     },
        //                 } as CartLineModel),
        //         );

        // addLinesToCart({
        //     apiParameter: {
        //         cartId: API_URL_CURRENT_FRAGMENT,
        //         cartLineCollection: { cartLines } as CartLineCollectionModel,
        //     },
        //     onSuccess: () => {
        //         toasterContext.addToast({
        //             body: (
        //                 <StyledWrapper>
        //                     {siteMessage("Hardwoods_OrderHistory_CannotAddToCart_Toaster")}
        //                 </StyledWrapper>
        //             ),
        //             messageType: "success" });
        //     },
        // });
        // }
        // XNG-Changes: HSP-1144
        const selectedCartLines = quote.cartLines?.filter(o => checkedItems[o.id]) || [];
        const restrictedItems = quote.cartLines?.filter(o => o.isRestricted || !o.canAddToCart);
        const allowedItems = selectedCartLines.filter(o => !o.isRestricted && o.canAddToCart);

        const cartLines =
            allowedItems &&
            allowedItems.map(
                o =>
                    ({
                        ...o,
                        properties: {
                            ...o.properties,
                            QuoteNumber: quote.quoteNumber,
                        },
                    } as CartLineModel),
            );
        if (!cartLines.length) {
            return;
        }
        addLinesToCart({
            apiParameter: {
                cartId: API_URL_CURRENT_FRAGMENT,
                cartLineCollection: { cartLines } as CartLineCollectionModel,
            },
            onSuccess: () => {
                const addedCount = cartLines.length;
                const restrictedCount = restrictedItems?.length;

                if (restrictedCount === 0) {
                    toasterContext.addToast({ body: translate("Added to Cart"), messageType: "success" });
                } else {
                    const restrictedPartNumbers = restrictedItems?.map(item => item.erpNumber) || [];

                    const toastMessage = (
                        <StyledWrapper>
                            <Typography>
                                {translate(
                                    `${addedCount} items were added, the following ${restrictedCount} items were not added:`,
                                )}
                            </Typography>
                            <ul>
                                {restrictedPartNumbers?.map((partNumber, index) => (
                                    <li key={index}>{partNumber}</li>
                                ))}
                            </ul>
                        </StyledWrapper>
                    );
                    toasterContext.addToast({
                        body: <StyledWrapper>{toastMessage}</StyledWrapper>,
                        messageType: "success",
                    });
                }
            },
        });
    };

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <StyledWrapper {...styles.headerWrapper}>
                <Typography as="p" {...styles.countValueText}>
                    {quoteLines.length}
                    <Typography {...styles.countLabelText}>
                        {quoteLines.length > 1 ? translate("Products") : translate("Product")}
                    </Typography>
                </Typography>
                <StyledWrapper {...styles.addSelectedContainer}>
                    <Checkbox
                        {...styles.selectAllButton}
                        checked={selectAllState}
                        onChange={() => {
                            onSelectAllItems();
                        }}
                    >
                        {translate("Select All")}
                    </Checkbox>
                    <Button
                        {...styles.addSelectedButton}
                        onClick={onClickAddSelected}
                        disabled={isDisabledAddSelected}
                        data-test-selector="orderDetailsReorderButton"
                    >
                        {translate("Add Selected To Cart")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
            {quoteLines.map(quoteLine => (
                <RfqQuoteDetailsProposedDetailsGridItem
                    key={`${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
                    quote={quote}
                    quoteLine={quoteLine}
                    checkedItems={checkedItems}
                    onSelectItem={onSelectItem}
                    extendedStyles={styles.itemStyles}
                />
            ))}
            {showPricing && (
                <StyledWrapper {...styles.footerWrapper}>
                    <Typography {...styles.totalValueText} as="p" data-test-selector="order_subTotal">
                        <Typography {...styles.totalLabelText}>{translate("Total")}:</Typography>
                        {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                            ? quote.orderGrandTotalDisplay
                            : quote.orderSubTotalDisplay}
                    </Typography>

                    {enableVat && (
                        <>
                            <Typography as="p" {...styles.vatLabelText}>
                                {vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat"
                                    ? translate("Inc. VAT")
                                    : translate("Ex. VAT")}
                            </Typography>
                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                <>
                                    <Typography {...styles.totalWithoutVatText}>
                                        {quote.orderSubTotalDisplay}
                                    </Typography>
                                    <Typography as="p" {...styles.vatLabelText}>
                                        {translate("Ex. VAT")}
                                    </Typography>
                                </>
                            )}
                        </>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsProposedDetailsGrid);
